import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const PricingPage = () => {
  const [billingInterval, setBillingInterval] = useState('monthly');

  const plans = {
    solo: {
      name: 'Solo',
      monthlyPrice: 19,
      annualPrice: 209,
      description: 'Everything you need to run your business',
      color: 'blue',
      teamMembers: '1 user',
      features: [
        'Unlimited customers & jobs',
        'Invoicing & online payments',
        'Expense tracking',
        'Receipt scanner',
        'Schedule C tax reports',
        'Quarterly tax estimates',
        'Map view'
      ],
    },
    pro: {
      name: 'Pro',
      monthlyPrice: 49,
      annualPrice: 539,
      description: 'Route optimization and team management',
      color: 'green',
      teamMembers: 'Up to 15 users',
      popular: true,
      features: [
        'Everything in Solo, plus:',
        'Up to 15 team members',
        'Route optimization',
        'Automated email reminders',
        'Customer portal',
        'P&L financial reports'
      ],
    },
    enterprise: {
      name: 'Enterprise',
      monthlyPrice: 129,
      annualPrice: 1419,
      description: 'Full customization and unlimited scale',
      color: 'purple',
      teamMembers: 'Unlimited users',
      features: [
        'Everything in Pro, plus:',
        'Unlimited team members',
        'Custom branding',
        'Marketing automation',
        'API access',
        'Multi-location support',
        'Priority support'
      ],
    },
  };

  const getPrice = (plan) => billingInterval === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
  const getSavings = (plan) => (plan.monthlyPrice * 12) - plan.annualPrice;

  const colorClasses = {
    blue: {
      badge: 'bg-blue-100 text-blue-700',
      price: 'text-blue-600',
      border: 'border-gray-200 hover:border-blue-400',
      button: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50',
      check: 'text-blue-600'
    },
    green: {
      badge: 'bg-green-100 text-green-700',
      price: 'text-green-600',
      border: 'border-green-400',
      button: 'bg-green-600 text-white hover:bg-green-700',
      check: 'text-green-600'
    },
    purple: {
      badge: 'bg-purple-100 text-purple-700',
      price: 'text-purple-600',
      border: 'border-gray-200 hover:border-purple-400',
      button: 'border-2 border-purple-600 text-purple-600 hover:bg-purple-50',
      check: 'text-purple-600'
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-20 pb-12 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1600&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 to-blue-800/90 z-0" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            Simple, Honest Pricing
          </h1>
          <p className="text-lg md:text-xl mb-2 text-blue-100">
            Start at just $19/month. No hidden fees. Cancel anytime.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Billing Toggle */}
          <div className="flex justify-center mb-10">
            <div className="bg-white rounded-xl p-1 shadow-md inline-flex border border-gray-200">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-6 py-2.5 rounded-lg font-semibold transition-all text-sm ${
                  billingInterval === 'monthly'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('annual')}
                className={`px-6 py-2.5 rounded-lg font-semibold transition-all relative text-sm ${
                  billingInterval === 'annual'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                Annual
                <span className="absolute -top-2.5 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                  Save
                </span>
              </button>
            </div>
          </div>

          {/* Plan Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(plans).map(([key, plan]) => {
              const colors = colorClasses[plan.color];
              return (
                <div 
                  key={key}
                  className={`bg-white rounded-xl border-2 ${colors.border} p-6 transition-all relative ${
                    plan.popular ? 'shadow-xl md:scale-105' : 'hover:shadow-lg'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                        MOST POPULAR
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-6 mt-2">
                    <span className={`inline-block ${colors.badge} px-3 py-1 rounded-full text-sm font-semibold mb-3`}>
                      {plan.name.toUpperCase()}
                    </span>
                    <div className="mb-2">
                      <span className={`text-4xl font-bold ${colors.price}`}>
                        ${getPrice(plan)}
                      </span>
                      <span className="text-gray-500">
                        /{billingInterval === 'monthly' ? 'mo' : 'yr'}
                      </span>
                    </div>
                    {billingInterval === 'annual' && (
                      <p className="text-sm text-green-600 font-medium">
                        Save ${getSavings(plan)}/year
                      </p>
                    )}
                    <p className="text-gray-600 text-sm mt-2">{plan.description}</p>
                    <p className="text-gray-500 text-xs mt-1">{plan.teamMembers}</p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className={`w-5 h-5 ${colors.check} flex-shrink-0`} />
                        <span className={`text-gray-700 ${idx === 0 && key !== 'solo' ? 'font-semibold' : ''}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Link 
                    to="/register" 
                    className={`block w-full text-center py-3 rounded-lg font-semibold transition-colors ${colors.button}`}
                  >
                    Start Free Trial
                  </Link>
                </div>
              );
            })}
          </div>

          <p className="text-center text-gray-500 mt-8 text-sm">
            All plans include a 14-day free trial. No credit card required.
          </p>
        </div>
      </section>

      {/* What's Included - Mobile Friendly */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
            What's Included
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Core Features',
                subtitle: 'All plans',
                items: ['Unlimited customers', 'Job & lead management', 'Invoicing & payments', 'Expense tracking', 'Receipt scanner', 'Tax reports']
              },
              {
                title: 'Route & Automation',
                subtitle: 'Pro & Enterprise',
                items: ['Route optimization', 'GPS start location', 'Automated reminders', 'Payment follow-ups', 'Review requests', 'Customer portal']
              },
              {
                title: 'Enterprise Power',
                subtitle: 'Enterprise only',
                items: ['Unlimited team', 'Custom branding', 'Marketing automation', 'API access', 'Multi-location', 'Priority support']
              }
            ].map((group) => (
              <div key={group.title} className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-1">{group.title}</h3>
                <p className="text-sm text-gray-500 mb-4">{group.subtitle}</p>
                <ul className="space-y-2">
                  {group.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {[
              {
                q: 'What\'s included in the free trial?',
                a: 'Full access to all Enterprise features for 14 days. No credit card required. See exactly what each plan offers before you choose.'
              },
              {
                q: 'Do you charge per user?',
                a: 'Solo is single-user. Pro includes up to 15 team members, and Enterprise includes unlimited—all at flat monthly rates. No per-user fees.'
              },
              {
                q: 'Can I upgrade or downgrade anytime?',
                a: 'Yes! Upgrade instantly (prorated) or downgrade at your next billing cycle. No penalties or hoops to jump through.'
              },
              {
                q: 'What\'s the difference between Solo and Pro?',
                a: 'Pro adds route optimization, automated emails (reminders, follow-ups, review requests), team members, customer portal, and P&L reports.'
              },
              {
                q: 'Are there any contracts or setup fees?',
                a: 'No contracts, no setup fees. Pay month-to-month and cancel anytime. We earn your business every month.'
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-white p-5 rounded-lg border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-600 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-16 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1552664730-d307ca884978?w=1600&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-blue-900/80 z-0" />

        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-lg mb-6 text-blue-100">
            Join service professionals who've streamlined their business with SuddyFlow.
          </p>
          <Link 
            to="/register" 
            className="inline-flex items-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-700 shadow-lg transition-colors"
          >
            Start Your Free Trial
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-4 text-blue-200 text-sm">
            14 days free • No credit card required
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;